import type {
  RecommendationLabRankingWeightKey,
  RecommendationLabRankingWeights,
} from '@/models/RecommendationLabRankingPolicy';

export interface RecommendationLabRankingPercentages {
  vertexRelevance: number;
  personaFit: number;
  evidence: number;
  popularity: number;
  routineFit: number;
  diversity: number;
}

export interface RecommendationLabRankingPolicyIssue {
  code: 'ranking_weight_invalid' | 'ranking_weight_precision_invalid' | 'ranking_weight_total_invalid';
  message: string;
}

export const FALLBACK_RANKING_PERCENTAGES: RecommendationLabRankingPercentages = {
  vertexRelevance: 12,
  personaFit: 12,
  evidence: 47,
  popularity: 0,
  routineFit: 23,
  diversity: 6,
};

const RANKING_WEIGHT_KEYS: RecommendationLabRankingWeightKey[] = [
  'vertexRelevance',
  'personaFit',
  'evidence',
  'popularity',
  'routineFit',
  'diversity',
];

export function toRankingPercentages(
  weights: RecommendationLabRankingWeights | undefined,
): RecommendationLabRankingPercentages {
  if (!weights) {
    return { ...FALLBACK_RANKING_PERCENTAGES };
  }
  return {
    vertexRelevance: weights.vertexRelevance * 100,
    personaFit: weights.personaFit * 100,
    evidence: weights.evidence * 100,
    popularity: weights.popularity * 100,
    routineFit: weights.routineFit * 100,
    diversity: weights.diversity * 100,
  };
}

export function collectRankingPolicyIssues(
  percentages: RecommendationLabRankingPercentages,
): RecommendationLabRankingPolicyIssue[] {
  const values = RANKING_WEIGHT_KEYS.map((key) => percentages[key]);
  if (values.some((value) => !Number.isFinite(value) || value < 0 || value > 100) || percentages.popularity !== 0) {
    return [
      {
        code: 'ranking_weight_invalid',
        message:
          'Each weight must be between 0% and 100%, and popularity must remain 0% while Product Persona is excluded.',
      },
    ];
  }

  if (values.some((value) => Math.abs(value * 100 - Math.round(value * 100)) > 0.000001)) {
    return [
      {
        code: 'ranking_weight_precision_invalid',
        message: 'Weights may use at most two decimal places.',
      },
    ];
  }

  const totalBasisPoints = values.reduce((total, value) => total + Math.round(value * 100), 0);
  if (totalBasisPoints !== 10_000) {
    return [
      {
        code: 'ranking_weight_total_invalid',
        message: `Weights must total 100%. The current total is ${(totalBasisPoints / 100).toFixed(2)}%.`,
      },
    ];
  }
  return [];
}

export function toRankingWeights(percentages: RecommendationLabRankingPercentages): RecommendationLabRankingWeights {
  return {
    vertexRelevance: Math.round(percentages.vertexRelevance * 100) / 10_000,
    personaFit: Math.round(percentages.personaFit * 100) / 10_000,
    evidence: Math.round(percentages.evidence * 100) / 10_000,
    popularity: 0,
    routineFit: Math.round(percentages.routineFit * 100) / 10_000,
    diversity: Math.round(percentages.diversity * 100) / 10_000,
  };
}

export function rankingPercentagesEqual(
  left: RecommendationLabRankingPercentages,
  right: RecommendationLabRankingPercentages,
): boolean {
  return (
    collectRankingPolicyIssues(left).length === 0 &&
    collectRankingPolicyIssues(right).length === 0 &&
    RANKING_WEIGHT_KEYS.every((key) => Math.round(left[key] * 100) === Math.round(right[key] * 100))
  );
}

export function normalizeRankingPercentages(candidate: unknown): RecommendationLabRankingPercentages | undefined {
  if (typeof candidate !== 'object' || candidate === null) {
    return undefined;
  }
  const record = candidate as Record<string, unknown>;
  const percentages = {
    vertexRelevance: record['vertexRelevance'],
    personaFit: record['personaFit'],
    evidence: record['evidence'],
    popularity: record['popularity'],
    routineFit: record['routineFit'],
    diversity: record['diversity'],
  };
  if (Object.values(percentages).some((value) => typeof value !== 'number')) {
    return undefined;
  }
  const normalized = percentages as RecommendationLabRankingPercentages;
  return collectRankingPolicyIssues(normalized).length === 0 ? normalized : undefined;
}
